import type {
  DirectMessagingAccount,
  DirectMessagingConfigsResponse,
  DirectMessagingDraft,
  DirectMessagingFormalValidationResponse,
  DirectMessagingPollResponse,
  DirectMessagingSendResponse,
  DirectMessagingValidationIssue,
  DirectMessagingXdmPreviewResponse,
} from '@dev-console/direct-messaging';
import {
  DIRECT_MESSAGING_MAX_XML_BYTES,
  DIRECT_MESSAGING_SENDERS,
  DIRECT_MESSAGING_XDM_SUBJECT_MARKER,
  isDirectAddress,
  isDirectMessagingXmlFilename,
  validateDirectMessagingXml,
} from '@dev-console/direct-messaging';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CcdaDocumentView } from './direct-messaging/CcdaDocumentView';
import { browserXmlParser, formatXml, sha256Hex } from './direct-messaging/xml';
import { ModalShell } from '@/components/modals/modal-shell';
import { Tabs } from '@/components/ui/tabs';
import { api } from '@/config/api';

export const meta = {
  label: 'Direct Messaging',
  order: 5,
};

const VIEW_TABS = [
  { id: 'document', label: 'HL7 Rendered' },
  { id: 'xml', label: 'XML' },
  { id: 'validation', label: 'Validation' },
  { id: 'result', label: 'Send Result' },
];

const INPUT_CLASS =
  'w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-400';
const SECONDARY_BUTTON =
  'rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50';
const FORMAL_VALIDATION_PAGE_SIZE = 100;
const STRUCTURAL_VALIDATION_DEBOUNCE_MS = 250;

const ACCOUNT_LABELS: Record<DirectMessagingAccount, string> = {
  production: 'Production',
  test: 'Test (phiMail sandbox)',
};

interface FormalValidationState {
  result: DirectMessagingFormalValidationResponse | null;
  sourceXml: string | null;
  error: string | null;
  running: boolean;
}

function byteCount(xml: string): number {
  return new TextEncoder().encode(xml).byteLength;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MiB`;
  return `${(bytes / 1024).toFixed(bytes < 1024 * 100 ? 1 : 0)} KiB`;
}

function newRequestId(): string {
  return window.crypto.randomUUID();
}

function ValidationIssues({ issues }: { issues: DirectMessagingValidationIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className='space-y-2'>
      {issues.map((issue, index) => (
        <li
          className={`rounded-md border px-3 py-2 text-sm ${
            issue.severity === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
          key={`${issue.code}-${index}`}
        >
          <div className='font-medium'>{issue.message}</div>
          <div className='mt-1 text-xs opacity-75'>
            {issue.code}
            {issue.path ? ` · ${issue.path}` : ''}
            {issue.line ? ` · line ${issue.line}` : ''}
            {issue.unconditional ? ' · cannot be overridden' : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}

function StructuralValidationPanel({
  pending,
  validation,
}: {
  pending: boolean;
  validation: ReturnType<typeof validateDirectMessagingXml>;
}) {
  return (
    <div className='space-y-4'>
      {pending ? (
        <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800'>
          Checking the latest XML. The results below are from the previous completed check.
        </div>
      ) : null}
      <div className='grid gap-3 sm:grid-cols-3'>
        <div className='rounded-md border border-neutral-200 bg-white p-3'>
          <div className='text-xs uppercase tracking-wide text-neutral-400'>Structural state</div>
          <div className={`mt-1 text-sm font-semibold ${validation.isValid ? 'text-green-700' : 'text-red-700'}`}>
            {validation.isValid ? 'Valid' : `${validation.errors.length} error(s)`}
          </div>
        </div>
        <div className='rounded-md border border-neutral-200 bg-white p-3'>
          <div className='text-xs uppercase tracking-wide text-neutral-400'>Document type</div>
          <div className='mt-1 text-sm font-semibold text-neutral-800'>{validation.documentType}</div>
        </div>
        <div className='rounded-md border border-neutral-200 bg-white p-3'>
          <div className='text-xs uppercase tracking-wide text-neutral-400'>Transfer command</div>
          <div className='mt-1 text-sm font-semibold text-neutral-800'>ADD {validation.attachmentMode}</div>
        </div>
      </div>
      {validation.errors.length === 0 && validation.warnings.length === 0 ? (
        <div className='rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800'>
          All continuous structural checks passed.
        </div>
      ) : null}
      <ValidationIssues issues={validation.errors} />
      <ValidationIssues issues={validation.warnings} />
    </div>
  );
}

interface PaginatedIssueListProps<T> {
  issues: T[];
  label: string;
  listClassName: string;
  renderIssue: (issue: T, index: number) => ReactNode;
}

function PaginatedIssueList<T>({ issues, label, listClassName, renderIssue }: PaginatedIssueListProps<T>) {
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [issues]);

  if (issues.length === 0) return null;
  const pageCount = Math.ceil(issues.length / FORMAL_VALIDATION_PAGE_SIZE);
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * FORMAL_VALIDATION_PAGE_SIZE;
  const pageEnd = Math.min(pageStart + FORMAL_VALIDATION_PAGE_SIZE, issues.length);

  return (
    <div>
      <h4 className='mb-2 text-sm font-semibold text-neutral-800'>{label}</h4>
      <ul className={listClassName}>
        {issues.slice(pageStart, pageEnd).map((issue, index) => renderIssue(issue, pageStart + index))}
      </ul>
      {pageCount > 1 ? (
        <div className='mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500'>
          <span>
            Showing {pageStart + 1}–{pageEnd} of {issues.length} {label.toLowerCase()}
          </span>
          <div className='flex gap-2'>
            <button
              aria-label={`Previous ${label.toLowerCase()} page`}
              className={SECONDARY_BUTTON}
              disabled={currentPage === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              type='button'
            >
              Previous
            </button>
            <button
              aria-label={`Next ${label.toLowerCase()} page`}
              className={SECONDARY_BUTTON}
              disabled={currentPage === pageCount - 1}
              onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
              type='button'
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FormalValidationPanel({ state }: { state: FormalValidationState }) {
  if (state.running) {
    return <div className='rounded-md bg-neutral-50 p-4 text-sm text-neutral-600'>Running the pinned profile…</div>;
  }
  if (state.error) {
    return <div className='rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800'>{state.error}</div>;
  }
  if (!state.result) {
    return (
      <div className='rounded-md bg-neutral-50 p-4 text-sm text-neutral-600'>
        Run the advisory Schematron profile when you want deeper C-CDA checks. It is not required to send.
      </div>
    );
  }

  const { result } = state;
  return (
    <div className='space-y-3'>
      <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800'>
        {result.profile} · {result.durationMs.toLocaleString()} ms. This report is advisory and does not claim receiver
        conformance.
      </div>
      <div className='grid grid-cols-3 gap-3 text-center'>
        <div className='rounded-md bg-red-50 p-2 text-sm text-red-800'>{result.errors.length} errors</div>
        <div className='rounded-md bg-amber-50 p-2 text-sm text-amber-800'>{result.warnings.length} warnings</div>
        <div className='rounded-md bg-neutral-100 p-2 text-sm text-neutral-700'>{result.ignored.length} ignored</div>
      </div>
      {(
        [
          { label: 'Errors', issues: result.errors, borderClass: 'border-red-200' },
          { label: 'Warnings', issues: result.warnings, borderClass: 'border-amber-200' },
        ] as const
      ).map(({ label, issues, borderClass }) => (
        <PaginatedIssueList
          issues={issues}
          key={label}
          label={label}
          listClassName='max-h-80 space-y-2 overflow-y-auto pr-1'
          renderIssue={(issue, index) => (
            <li
              className={`rounded-md border bg-white p-3 text-sm ${borderClass}`}
              key={`${issue.assertionId}-${index}`}
            >
              <div className='text-neutral-800'>{issue.description}</div>
              <div className='mt-1 break-all font-mono text-xs text-neutral-500'>
                {issue.assertionId ?? 'no assertion id'}
                {issue.path ? ` · ${issue.path}` : ''}
                {issue.line ? ` · line ${issue.line}` : ''}
              </div>
            </li>
          )}
        />
      ))}
      <PaginatedIssueList
        issues={result.ignored}
        label='Unsupported or ignored checks'
        listClassName='max-h-64 space-y-2 overflow-y-auto pr-1'
        renderIssue={(issue, index) => (
          <li
            className='rounded-md border border-neutral-200 bg-white p-3 text-sm'
            key={`${issue.assertionId}-${index}`}
          >
            <div className='text-neutral-800'>{issue.description}</div>
            <div className='mt-1 text-xs text-neutral-500'>{issue.reason}</div>
          </li>
        )}
      />
    </div>
  );
}

function SendResultPanel({ result }: { result: DirectMessagingSendResponse | null }) {
  if (!result) {
    return (
      <div className='rounded-md bg-neutral-50 p-4 text-sm text-neutral-600'>
        No send has been attempted in this browser session.
      </div>
    );
  }
  const color =
    result.outcome === 'queued'
      ? 'border-green-200 bg-green-50 text-green-800'
      : result.outcome === 'rejected'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-red-200 bg-red-50 text-red-800';
  return (
    <div className='space-y-4'>
      <div className={`rounded-md border p-4 ${color}`}>
        <div className='text-sm font-semibold uppercase tracking-wide'>{result.outcome}</div>
        <div className='mt-1 text-sm'>{result.summary}</div>
        {result.externalMessageId ? (
          <div className='mt-2 break-all font-mono text-xs'>External message ID: {result.externalMessageId}</div>
        ) : null}
        {result.cached ? <div className='mt-2 text-xs'>Returned from the duplicate-prevention cache.</div> : null}
      </div>
      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='rounded-md border border-neutral-200 p-3 text-sm'>Duration: {result.durationMs} ms</div>
        <div className='rounded-md border border-neutral-200 p-3 text-sm'>
          {result.xdm
            ? `Attachment: ${result.xdm.filename} (${formatBytes(result.xdm.zipByteCount)} XDM zip)`
            : `Attachment: ${formatBytes(result.byteCount)}`}
        </div>
      </div>
      {result.xdm ? (
        <details className='rounded-md border border-neutral-200 p-3 text-sm'>
          <summary className='cursor-pointer text-neutral-700'>Sent METADATA.XML</summary>
          <pre className='mt-2 max-h-64 overflow-auto rounded-md bg-neutral-950 p-2 text-[10px] leading-4 text-green-100'>
            {result.xdm.metadataXml}
          </pre>
        </details>
      ) : null}
      {result.failureCategory ? (
        <div className='rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700'>
          Failure category: <span className='font-medium'>{result.failureCategory}</span>
        </div>
      ) : null}
    </div>
  );
}

export function DirectMessagingPage() {
  const { data: configs, error: configError } = useQuery({
    queryKey: ['direct-messaging-config'],
    queryFn: () => api<DirectMessagingConfigsResponse>('/dev-console/direct-messaging/config'),
  });
  const [account, setAccount] = useState<DirectMessagingAccount>('production');
  const config = configs?.[account];
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('[TEST] C-CDA Document Attached');
  const [body, setBody] = useState('PHI-free interoperability test from Nourish.');
  const [filename, setFilename] = useState('nourish-direct-test.xml');
  const [xml, setXml] = useState('');
  const [requestDeliveryNotification, setRequestDeliveryNotification] = useState(true);
  const [packageAsXdm, setPackageAsXdm] = useState(true);
  const [xdmPreview, setXdmPreview] = useState<DirectMessagingXdmPreviewResponse | null>(null);
  const [xdmPreviewLoading, setXdmPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('xml');
  const [editorNotice, setEditorNotice] = useState<string | null>(null);
  const [sha256, setSha256] = useState<string | null>(null);
  const [formalValidation, setFormalValidation] = useState<FormalValidationState>({
    result: null,
    sourceXml: null,
    error: null,
    running: false,
  });
  const [sendResult, setSendResult] = useState<DirectMessagingSendResponse | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [allowInvalid, setAllowInvalid] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [clientRequestId, setClientRequestId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState<DirectMessagingPollResponse | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationInput, setValidationInput] = useState(() => ({ recipient, xml }));

  const validation = useMemo(
    () => validateDirectMessagingXml(validationInput.xml, validationInput.recipient, browserXmlParser),
    [validationInput]
  );
  const recipientMismatch = validation.warnings.find((issue) => issue.code === 'recipient_mismatch');
  const xmlByteCount = useMemo(() => byteCount(validationInput.xml), [validationInput.xml]);
  const validationPending = validationInput.xml !== xml || validationInput.recipient !== recipient;

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setValidationInput({ recipient, xml }),
      STRUCTURAL_VALIDATION_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [recipient, xml]);

  useEffect(() => {
    let cancelled = false;
    setSha256(null);
    void sha256Hex(xml)
      .then((hash) => {
        if (!cancelled) setSha256(hash);
      })
      .catch(() => {
        if (!cancelled) setEditorNotice('SHA-256 is unavailable in this browser context, so sending is disabled.');
      });
    return () => {
      cancelled = true;
    };
  }, [xml]);

  useEffect(() => {
    setClientRequestId(null);
  }, [account, body, filename, packageAsXdm, recipient, requestDeliveryNotification, subject, xml]);

  useEffect(() => {
    setXdmPreview(null);
  }, [xml]);

  const draft = useMemo<DirectMessagingDraft>(
    () => ({
      recipient,
      subject,
      body,
      filename,
      xml,
      requestDeliveryNotification,
      packageAsXdm,
    }),
    [body, filename, packageAsXdm, recipient, requestDeliveryNotification, subject, xml]
  );

  async function loadXdmPreview() {
    if (xdmPreviewLoading || !xml) return;
    setXdmPreviewLoading(true);
    try {
      const result = await api<DirectMessagingXdmPreviewResponse>('/dev-console/direct-messaging/xdm-preview', {
        method: 'POST',
        body: JSON.stringify({ xml }),
      });
      setXdmPreview(result);
    } catch {
      setXdmPreview({ error: 'The XDM metadata preview could not be generated.', metadataXml: null });
    } finally {
      setXdmPreviewLoading(false);
    }
  }

  function loadLocalFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.xml')) {
      setEditorNotice('Choose a local .xml file.');
      return;
    }
    if (file.size > DIRECT_MESSAGING_MAX_XML_BYTES) {
      setEditorNotice(
        `${file.name} is ${formatBytes(file.size)}. Local XML files must be ${formatBytes(
          DIRECT_MESSAGING_MAX_XML_BYTES
        )} or smaller.`
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        setEditorNotice('The local file could not be read as text.');
        return;
      }
      setFilename(file.name);
      setXml(reader.result);
      setActiveTab('xml');
      setEditorNotice(`Loaded ${file.name} locally. Nothing was uploaded.`);
    };
    reader.onerror = () => setEditorNotice('The local file could not be read.');
    reader.readAsText(file);
  }

  function formatEditorXml() {
    try {
      setXml(formatXml(xml));
      setEditorNotice('Formatted and reserialized the editor XML. This intentionally changes its bytes.');
    } catch (error) {
      setEditorNotice(error instanceof Error ? error.message : 'XML formatting failed.');
    }
  }

  async function copyXml() {
    await navigator.clipboard.writeText(xml);
    setEditorNotice('Copied XML to the clipboard.');
  }

  function downloadXml() {
    const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document.xml';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function runFormalValidation() {
    setFormalValidation({ result: null, sourceXml: null, error: null, running: true });
    try {
      const result = await api<DirectMessagingFormalValidationResponse>('/dev-console/direct-messaging/validate', {
        method: 'POST',
        body: JSON.stringify({ xml }),
      });
      setFormalValidation({ result, sourceXml: xml, error: null, running: false });
    } catch {
      setFormalValidation({
        result: null,
        sourceXml: null,
        error: 'The advisory Schematron report could not be completed. Fix structural XML errors and try again.',
        running: false,
      });
    }
  }

  function openConfirmation() {
    setSendError(null);
    setAllowInvalid(false);
    setClientRequestId((current) => current ?? newRequestId());
    setConfirmationOpen(true);
  }

  async function confirmSend() {
    if (!clientRequestId || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await api<DirectMessagingSendResponse>('/dev-console/direct-messaging/send', {
        method: 'POST',
        body: JSON.stringify({ draft, account, allowInvalid, clientRequestId }),
      });
      setSendResult(result);
      setConfirmationOpen(false);
      setActiveTab('result');
      setClientRequestId(null);
    } catch {
      setSendError('The request could not be completed. Retrying here will reuse the same duplicate-prevention ID.');
    } finally {
      setSending(false);
    }
  }

  async function pollInbox() {
    if (polling) return;
    setPolling(true);
    setPollResult(null);
    setPollError(null);
    try {
      const result = await api<DirectMessagingPollResponse>('/dev-console/direct-messaging/poll', {
        method: 'POST',
        body: JSON.stringify({ account }),
      });
      setPollResult(result);
    } catch {
      setPollError('The inbox poll could not be queued. Try again or check the job queues.');
    } finally {
      setPolling(false);
    }
  }

  const maxSubjectLength = config?.limits.maxSubjectLength ?? 255;
  const xdmSubjectSuffixLength =
    packageAsXdm && !subject.includes(DIRECT_MESSAGING_XDM_SUBJECT_MARKER)
      ? DIRECT_MESSAGING_XDM_SUBJECT_MARKER.length + 1
      : 0;
  const subjectInputMaxLength = Math.max(maxSubjectLength - xdmSubjectSuffixLength, 0);
  const subjectTooLong = subject.length > subjectInputMaxLength;
  const canReview =
    config?.ready === true &&
    !validationPending &&
    isDirectAddress(recipient) &&
    !!subject &&
    !subjectTooLong &&
    isDirectMessagingXmlFilename(filename) &&
    !!xml &&
    xmlByteCount <= DIRECT_MESSAGING_MAX_XML_BYTES &&
    sha256 !== null;
  const formalIsStale = formalValidation.result !== null && formalValidation.sourceXml !== xml;
  const sendBlocked =
    !validation.canOverride || (validation.errors.length > 0 && !allowInvalid) || sending || !clientRequestId;
  const formalValidationBlocked =
    validationPending ||
    validation.errors.some((issue) =>
      ['empty_xml', 'forbidden_xml_declaration', 'malformed_xml', 'payload_too_large'].includes(issue.code)
    );

  return (
    <div className='mx-auto max-w-[1600px] space-y-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='text-xl font-semibold text-neutral-900'>EMR Direct / C-CDA Debugger</h2>
          <p className='mt-1 text-sm text-neutral-500'>
            Build and inspect an in-memory XML attachment. Outbound test sends do not write to S3 or the database; inbox
            polling uses the normal Direct Messaging pipeline.
          </p>
        </div>
        <div className='flex gap-2'>
          <button className={SECONDARY_BUTTON} disabled={polling} onClick={pollInbox} type='button'>
            {polling ? 'Queueing poll…' : 'Poll Inbox'}
          </button>
          <button
            className='rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-neutral-300'
            disabled={!canReview}
            onClick={openConfirmation}
            type='button'
          >
            Review send
          </button>
        </div>
      </div>

      {pollResult ? (
        <div className='rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800'>
          Queued the existing <span className='font-mono'>checkDsmInbox</span> job. The{' '}
          {pollResult.account === 'production' ? 'production' : 'staging'} worker will poll and process the{' '}
          {ACCOUNT_LABELS[pollResult.account]} inbox through the normal Direct Messaging pipeline.{' '}
          <a
            className='font-medium underline'
            href={`/job-worker-traces?traceId=${encodeURIComponent(pollResult.traceId)}&environment=${
              pollResult.account === 'production' ? 'production' : 'staging'
            }`}
          >
            View job trace
          </a>
        </div>
      ) : null}
      {pollError ? (
        <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800'>{pollError}</div>
      ) : null}

      {configError ? (
        <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800'>
          Could not load Direct Messaging configuration.
        </div>
      ) : null}
      {!config && !configError ? (
        <div className='rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600'>
          Loading Direct Messaging configuration…
        </div>
      ) : null}
      {config && !config.ready ? (
        <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
          <div className='font-semibold'>The {ACCOUNT_LABELS[account]} sending account is not ready.</div>
          <div className='mt-1'>
            {config.enabled ? 'Missing or invalid server variables:' : 'The fail-closed enable flag is off:'}{' '}
            {config.missingVariables.join(', ') || 'DEV_CONSOLE_DIRECT_MESSAGING_ENABLED'}
          </div>
        </div>
      ) : null}
      {config?.ready ? (
        <div className='flex flex-wrap gap-x-5 gap-y-1 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800'>
          <span>Ready · {ACCOUNT_LABELS[account]}</span>
          <span>
            Host: {config.host}:{config.port}
          </span>
          <span>Certificate valid through {config.certificate?.validTo ?? 'unknown'}</span>
        </div>
      ) : null}

      <div className='grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]'>
        <aside className='bg-neutral-25 space-y-4 rounded-lg border border-neutral-200 p-4'>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-account'>
              Sending account
            </label>
            <select
              className={INPUT_CLASS}
              id='direct-account'
              onChange={(event) => setAccount(event.target.value as DirectMessagingAccount)}
              value={account}
            >
              <option value='production'>{ACCOUNT_LABELS.production}</option>
              <option value='test'>{ACCOUNT_LABELS.test}</option>
            </select>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-sender'>
              Sender
            </label>
            <input
              className={`${INPUT_CLASS} bg-neutral-100 text-neutral-500`}
              id='direct-sender'
              readOnly
              value={config?.sender ?? DIRECT_MESSAGING_SENDERS[account]}
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-recipient'>
              Recipient Direct address
            </label>
            <input
              className={INPUT_CLASS}
              id='direct-recipient'
              onChange={(event) => setRecipient(event.target.value)}
              placeholder='recipient@direct.example.org'
              spellCheck={false}
              value={recipient}
            />
            {recipient && !isDirectAddress(recipient) ? (
              <div className='mt-1 text-xs text-red-600'>Enter a syntactically valid Direct address.</div>
            ) : null}
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-subject'>
              Subject
            </label>
            <input
              className={INPUT_CLASS}
              id='direct-subject'
              maxLength={subjectInputMaxLength}
              onChange={(event) => setSubject(event.target.value)}
              value={subject}
            />
            {subjectTooLong ? (
              <div className='mt-1 text-xs text-red-600'>
                Subject must not exceed {subjectInputMaxLength} characters when packaging as XDM.
              </div>
            ) : packageAsXdm ? (
              <div className='mt-1 text-xs text-neutral-400'>
                The sent subject will include the {DIRECT_MESSAGING_XDM_SUBJECT_MARKER} marker required for XDM.
              </div>
            ) : null}
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-body'>
              Body
            </label>
            <textarea
              className={`${INPUT_CLASS} resize-y`}
              id='direct-body'
              maxLength={config?.limits.maxBodyLength ?? 10_000}
              onChange={(event) => setBody(event.target.value)}
              rows={3}
              value={body}
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-500' htmlFor='direct-filename'>
              XML filename
            </label>
            <input
              className={INPUT_CLASS}
              id='direct-filename'
              onChange={(event) => setFilename(event.target.value)}
              spellCheck={false}
              value={filename}
            />
            {filename && !isDirectMessagingXmlFilename(filename) ? (
              <div className='mt-1 text-xs text-red-600'>Use a simple .xml filename without spaces or folders.</div>
            ) : null}
          </div>
          <label className='flex items-center gap-2 text-sm text-neutral-700'>
            <input
              checked={requestDeliveryNotification}
              className='accent-orange-500'
              onChange={(event) => setRequestDeliveryNotification(event.target.checked)}
              type='checkbox'
            />
            Request delivery notification
          </label>
          <label className='flex items-center gap-2 text-sm text-neutral-700'>
            <input
              checked={packageAsXdm}
              className='accent-orange-500'
              onChange={(event) => setPackageAsXdm(event.target.checked)}
              type='checkbox'
            />
            Package as IHE XDM zip
          </label>
          {packageAsXdm ? (
            <div className='space-y-2'>
              <div className='text-xs text-neutral-400'>
                Clinical documents use the shared XDM metadata generator. XDM sourcePatientId is derived from the
                existing C-CDA patientRole ID. Uncheck to send the raw XML for debugging.
              </div>
              <button
                className={`${SECONDARY_BUTTON} w-full`}
                disabled={xdmPreviewLoading || !xml}
                onClick={() => void loadXdmPreview()}
                type='button'
              >
                {xdmPreviewLoading ? 'Generating…' : 'Preview METADATA.XML'}
              </button>
              {xdmPreview?.error ? <div className='text-xs text-red-600'>{xdmPreview.error}</div> : null}
              {xdmPreview?.metadataXml ? (
                <pre className='max-h-64 overflow-auto rounded-md border border-neutral-200 bg-neutral-950 p-2 text-[10px] leading-4 text-green-100'>
                  {xdmPreview.metadataXml}
                </pre>
              ) : null}
            </div>
          ) : null}

          <div className='border-t border-neutral-200 pt-4'>
            <input
              accept='.xml,application/xml,text/xml'
              className='hidden'
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) loadLocalFile(file);
                event.target.value = '';
              }}
              ref={fileInputRef}
              type='file'
            />
            <button
              className={`${SECONDARY_BUTTON} w-full`}
              onClick={() => fileInputRef.current?.click()}
              type='button'
            >
              Load local XML file
            </button>
            <div className='mt-2 text-xs text-neutral-400'>FileReader keeps the selected file in this browser.</div>
          </div>
        </aside>

        <section className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white'>
          <div className='flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-3'>
            <Tabs activeTab={activeTab} onTabChange={setActiveTab} tabs={VIEW_TABS} />
            <div className='flex flex-wrap items-center gap-2 py-2 text-xs text-neutral-500'>
              <span>{formatBytes(xmlByteCount)}</span>
              <span>ADD {validation.attachmentMode}</span>
              <span
                className={validationPending ? 'text-blue-700' : validation.isValid ? 'text-green-700' : 'text-red-700'}
              >
                {validationPending
                  ? 'checking latest XML…'
                  : validation.isValid
                  ? 'structurally valid'
                  : `${validation.errors.length} error(s)`}
              </span>
            </div>
          </div>
          {editorNotice ? (
            <div className='border-b border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-800'>{editorNotice}</div>
          ) : null}
          <div className='min-h-[620px] p-4'>
            {activeTab === 'document' ? <CcdaDocumentView xml={xml} /> : null}
            {activeTab === 'xml' ? (
              <div className='space-y-3'>
                <div className='flex flex-wrap gap-2'>
                  <button className={SECONDARY_BUTTON} onClick={formatEditorXml} type='button'>
                    Format XML
                  </button>
                  <button className={SECONDARY_BUTTON} onClick={() => void copyXml()} type='button'>
                    Copy
                  </button>
                  <button className={SECONDARY_BUTTON} onClick={downloadXml} type='button'>
                    Save local copy
                  </button>
                </div>
                <textarea
                  aria-label='C-CDA XML editor'
                  className='h-[560px] w-full resize-y rounded-md border border-neutral-200 bg-neutral-950 p-4 font-mono text-xs leading-5 text-green-100 outline-none focus:border-neutral-400'
                  onChange={(event) => setXml(event.target.value)}
                  placeholder='Paste C-CDA or other XML here…'
                  spellCheck={false}
                  value={xml}
                />
              </div>
            ) : null}
            {activeTab === 'validation' ? (
              <div className='space-y-6'>
                <StructuralValidationPanel pending={validationPending} validation={validation} />
                <div className='border-t border-neutral-200 pt-5'>
                  <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
                    <div>
                      <h3 className='text-base font-semibold text-neutral-900'>Advisory C-CDA 2.1 Schematron</h3>
                      <p className='mt-0.5 text-xs text-neutral-500'>
                        Runs in an isolated worker with a timeout. Unsupported assertions are shown explicitly.
                      </p>
                    </div>
                    <button
                      className={SECONDARY_BUTTON}
                      disabled={formalValidation.running || formalValidationBlocked}
                      onClick={() => void runFormalValidation()}
                      type='button'
                    >
                      {formalValidation.running ? 'Running…' : 'Run formal validation'}
                    </button>
                  </div>
                  {formalIsStale ? (
                    <div className='mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800'>
                      The XML changed after this report was generated. Run it again for current results.
                    </div>
                  ) : null}
                  <FormalValidationPanel state={formalValidation} />
                </div>
              </div>
            ) : null}
            {activeTab === 'result' ? <SendResultPanel result={sendResult} /> : null}
          </div>
        </section>
      </div>

      {confirmationOpen && sha256 ? (
        <ModalShell
          footer={
            <>
              <button
                className={SECONDARY_BUTTON}
                disabled={sending}
                onClick={() => setConfirmationOpen(false)}
                type='button'
              >
                Cancel
              </button>
              <button
                className='rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-neutral-300'
                disabled={sendBlocked}
                onClick={() => void confirmSend()}
                type='button'
              >
                {sending ? 'Submitting…' : 'Submit to EMR Direct'}
              </button>
            </>
          }
          onClose={() => {
            if (!sending) setConfirmationOpen(false);
          }}
          title='Confirm outbound Direct message'
        >
          <div className='space-y-3'>
            <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
              EMR Direct accepting the message means queued for delivery—not delivered. This tool does not poll
              receipts.
            </div>
            {recipientMismatch ? (
              <div className='rounded-md border-2 border-red-300 bg-red-50 p-3 text-xs text-red-950' role='alert'>
                <div className='font-semibold'>Recipient mismatch</div>
                <p className='mt-1'>The outbound Direct address and the C-CDA informationRecipient are different.</p>
                <dl className='mt-2 space-y-1'>
                  <div>
                    <dt className='inline font-medium'>Envelope recipient: </dt>
                    <dd className='inline break-all'>{recipient}</dd>
                  </div>
                  <div>
                    <dt className='inline font-medium'>C-CDA recipient: </dt>
                    <dd className='inline break-all'>{validation.informationRecipient}</dd>
                  </div>
                </dl>
                <p className='mt-2 font-medium'>Verify both addresses before submitting.</p>
              </div>
            ) : null}
            <dl className='space-y-2 text-xs'>
              {[
                ['Account', ACCOUNT_LABELS[account]],
                ['Sender', config?.sender ?? DIRECT_MESSAGING_SENDERS[account]],
                ['Recipient', recipient],
                ['Filename', filename],
                ['Bytes', xmlByteCount.toLocaleString()],
                ['SHA-256', sha256],
                [
                  'Validation',
                  validation.isValid
                    ? recipientMismatch
                      ? 'Passed with recipient mismatch warning'
                      : 'Passed'
                    : `${validation.errors.length} structural error(s)`,
                ],
                ['Transfer', `ADD ${validation.attachmentMode}`],
                ['Override', validation.isValid ? 'Not needed' : allowInvalid ? 'Enabled' : 'Not enabled'],
              ].map(([label, value]) => (
                <div className='grid grid-cols-[90px_minmax(0,1fr)] gap-2' key={label}>
                  <dt className='font-medium text-neutral-500'>{label}</dt>
                  <dd className={`break-all text-neutral-800 ${label === 'SHA-256' ? 'font-mono' : ''}`}>{value}</dd>
                </div>
              ))}
            </dl>
            {validation.errors.length > 0 ? (
              <label className='flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900'>
                <input
                  checked={allowInvalid}
                  className='mt-0.5 accent-red-600'
                  disabled={!validation.canOverride}
                  onChange={(event) => setAllowInvalid(event.target.checked)}
                  type='checkbox'
                />
                <span>
                  Send anyway despite structural errors. DTD/entity declarations and files over 5 MiB can never be
                  overridden.
                </span>
              </label>
            ) : null}
            {sendError ? <div className='rounded-md bg-red-50 p-3 text-xs text-red-800'>{sendError}</div> : null}
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}
